import React, { useCallback, useEffect, useState } from 'react';
import { Box, Grid } from '@material-ui/core'
import { Button } from "@paro.io/base-ui";
import { IconArrowUp, IconDocumentPlus } from '@paro.io/base-icons';
import { v4 as uuid_v4 } from 'uuid';
import { generateDownloadFile } from '../Template';
import { CellValue, Workbook } from 'exceljs';
import { CloudUploadOutlined, LockOutlined } from '@material-ui/icons';
import axios from 'axios'

type LinkButtonProps = {
    onClick: () => void;
    children: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
};

const LinkButton: React.FC<LinkButtonProps> = ({ onClick, children, className = '', style = {}, disabled = false }) => {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            className={`link-button ${className}`}
            style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                color: '#1a73e8',
                textDecoration: 'underline',
                cursor: disabled ? 'not-allowed' : 'pointer',
                font: 'inherit',
                ...style,
            }}
        >
            {children}
        </button>
    );
};

const transformCustomer = (customer) => ({
    firstName: customer.GivenName || '',
    lastName: customer.FamilyName || '',
    company: customer.CompanyName || '',
    email: customer.PrimaryEmailAddr?.Address || '',
});

const Step1 = ({ setImportedClients, history, setUpdateSteps, setCurrentStep, setFetchingQuickbooks, setAllClients, setAddNewClientModal }) => {

    const [file, setFile] = useState<File | null>(null);
    const [fileTypeError, setFileTypeError] = useState<boolean>(false);
    const [connectQuickbooks, setConnectQuickbooks] = useState<boolean>(false);
    const [loadingExpertPortal, setLoadingExpertPortal] = useState<boolean>(false);

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code'); // Get the authorization code
    const realmId = urlParams.get('realmId'); // Get the company ID (realmId)

    // Once the user is redirected back from QuickBooks, handle the callback and fetch customer data
    const getCustomerData = useCallback(async (code, realmId) => {
        setConnectQuickbooks(true);
        setFetchingQuickbooks(true);
        try {
            // Build the request body dynamically
            const payload = {
                query: { code, realmId },
                headers: { origin: window.location.origin },
                rawPath: window.location.pathname,
                rawQueryString: window.location.search.substring(1),
            };

            const res = await axios.post(
                `${process.env.REACT_APP_V2_ENDPOINT}/getQuickbooksClientsHandler`,
                payload
            );

            if (res.status === 200) {
                const lambdaResponse = res.data;
                const parsedBody = lambdaResponse?.body
                    ? JSON.parse(lambdaResponse.body)
                    : lambdaResponse;
                const customerArray = parsedBody?.QueryResponse?.Customer ?? [];
                const transformedCustomers = customerArray?.map(transformCustomer) ?? [];
                setAllClients(customerArray);
                setImportedClients(transformedCustomers);
            } else {
                console.error("Failed to fetch customer data:", res.statusText);
            }
        } catch (error) {
            console.error("Failed to fetch customer data:", error);
        } finally {
            //QuickBooks uses OAuth, and many OAuth providers (including Intuit) will sometimes open the consent/auth window in an iframe and it blocks clicks and pointer events on your page.
            // this will remove the iframe 
            document.querySelectorAll('iframe').forEach((el) => {
                const style = getComputedStyle(el);
                if (
                    style.position === 'fixed' &&
                    Number(style.zIndex) > 100 &&
                    style.pointerEvents !== 'none'
                ) {
                    console.warn('Removing blocking iframe:', el);
                    el.remove();
                }
            });
            setConnectQuickbooks(false);
            setFetchingQuickbooks(false);
            setTimeout(() => {
                history.push(`/registration/aigp-onboarding/quickbooks`);
            }, 50);
            setUpdateSteps(prev =>
                prev.map(obj =>
                    obj.number === 2 ? { ...obj, completed: true } : obj
                ))
            setCurrentStep(2);
        }
    }, [history, setCurrentStep, setImportedClients, setUpdateSteps, setAllClients, setFetchingQuickbooks]);

    useEffect(() => {
        if (code && realmId) {
            // this will fetch customer data from quickbooks
            getCustomerData(code, realmId);
        }
    }, [code, realmId, getCustomerData]);

    const syncQuickbooksData = async () => {

        setConnectQuickbooks(true);
        try {
            const res = await axios.post(
                `${process.env.REACT_APP_V2_ENDPOINT}/quickbooksAuthorizeURLHandler`
            );

            if (res.status === 200) {
                const { authUri } = res.data;

                if (authUri) {
                    window.location.href = authUri;
                } else {
                    console.error("Authorization URL missing in response");
                }
            } else {
                console.error("Error starting OAuth flow:", res.statusText);
            }
        } catch (err) {
            console.error("Failed to start QuickBooks OAuth flow:", err);
        } finally {
            setConnectQuickbooks(false);
        }
    };

    const handleUploadedFileChange = (event: any) => {
        const newFile = event.target.files[0]
        setFileTypeError(false)
        event.target.value = null;
        if (newFile) {
            const fileType = newFile.name.split('.').pop()
            if (fileType.toLowerCase() !== 'xlsx') {
                setFileTypeError(true)
                return
            }
        }
        setFile(newFile);
        if (newFile) { handleUpload() };
    }

    const handleUpload = () => {

        const data: (CellValue[] | { [key: string]: CellValue; })[] = [];
        const resultData = { data }
        const wb = new Workbook();
        const reader = new FileReader()
        if (file) {
            reader.readAsArrayBuffer(file)
            reader.onload = () => {
                const buffer = reader.result;
                //@ts-ignore
                wb.xlsx.load(buffer).then(async workbook => {

                    let sheet = workbook.getWorksheet(1);

                    if (sheet) {
                        //@ts-ignore
                        sheet.eachRow({
                            includeEmpty: false
                        }, (row, rowIndex) => {
                            let record: string[] = ["", "", "", ""]
                            row.eachCell((cell, colNumber) => {
                                if (colNumber > 0) {
                                    const cellValue = cell.value;
                                    //@ts-ignore
                                    record[colNumber - 1] = cellValue;
                                }
                            });

                            resultData.data[rowIndex] = record;
                        })

                        resultData.data.shift()
                        await handleCsvParsed(resultData).catch(error => {
                            console.error(error)
                        });
                    }
                }).catch(async error => {
                    console.error(`Error with Calling Upload Clients data: ${error}`)
                });
            }
        }
    }

    const handleCsvParsed = async (resultData: any) => {
        const mappedData = [...resultData.data.slice(1)].map((obj) => {
            return {
                firstName: obj[0] || '',
                lastName: obj[1] || '',
                email: obj[2].text || '',
                company: obj[3] || '',
            }
        })
        setImportedClients([...mappedData]);
        setAllClients([...mappedData]);
        setUpdateSteps(prev =>
            prev.map(obj =>
                obj.number === 2 ? { ...obj, completed: true } : obj
            ))
        setFetchingQuickbooks(true);
        setTimeout(() => { setFetchingQuickbooks(false); setCurrentStep(2); }, 1000);
        return;
    }

    const downloadCSV = async () => {
        let id = uuid_v4()
        let workbook = await generateDownloadFile();
        localStorage.setItem("client-workbook-id", id)
        workbook.xlsx.writeBuffer().then((blob: BlobPart) => {
            let data = new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            let csvURL = window.URL.createObjectURL(data);
            let tempLink = document.createElement('a');
            tempLink.href = csvURL;
            tempLink.setAttribute('download', 'ClientDataTemplate.xlsx');
            tempLink.click();
        })
            .catch(error => {
                console.error(error);
            });
    }

    return (
        <Box sx={{ flex: 1, padding: 50 }}>
            <Grid container spacing={2} style={{ paddingBottom: 6 }}>
                <Grid item xs={12}>
                    <h1 className="text-xl font-semibold">Connect Quickbooks</h1>
                    <p className="text-sm mt-1">Securely connect to QuickBooks to import your clients in minutes and start managing your firm through our platform right away.</p>
                    <p className="text-md font-semibold mt-4">You’ll get:</p>
                    <ul style={{ margin: '2px 0 8px 30px', listStyleType: 'disc' }}>
                        <li>Streamlined operations from day one</li>
                        <li>Enhanced client service capabilities without changing your relationships</li>
                        <li>Better project oversight so you can drive more revenue</li>
                    </ul>
                    <div className="flex justify-center items-center">
                        <Button
                            label="Connect to Quickbooks"
                            className="mt-2"
                            onClick={syncQuickbooksData}
                            color="primary"
                            isLoading={connectQuickbooks}
                        />
                    </div>
                    <div><p className="text-sm mt-4">Don't use QuickBooks? You can still upload a CSV—it only takes a few key details to get started. <LinkButton onClick={downloadCSV}>Download template</LinkButton></p></div>
                    <div className="flex flex-col bg-white mx-auto justify-center items-center p-2 border-2 border-gray-500 rounded-md space-y-2">
                        <CloudUploadOutlined className="text-success-dark" fontSize={'large'} />
                        <div className="flex justify-start mt-4">
                            <label htmlFor="fileInput" className="flex self-center bg-white hover:bg-gray-200 text-black py-2 px-4 inline-block cursor-pointer border-2 border-r-0 border-[#CBD5E1] rounded-l-md"><IconArrowUp size='sm' />&nbsp;BROWSE</label>
                            <p className="flex self-center py-2 px-4 border-l-0 border-2 border-[#CBD5E1]">{file?.name ?? "Choose File"}</p>
                        </div>
                        <input type="file" id="fileInput" accept=".xlsx" required={true} onChange={handleUploadedFileChange} className='hidden' />
                        {fileTypeError &&
                            <div className='flex items-center w-full ml-4 text-danger-light'>
                                Make sure to upload an xlsx format. <LinkButton onClick={downloadCSV}>Download template</LinkButton>
                            </div>
                        }
                    </div>
                    <p className="mt-4">Having trouble? Contact <span><a href="mailto:support@paro.ai" className="underline" style={{ color: '#248384' }}>support@paro.ai</a></span> for help.</p>
                    <div className="flex flex-col justify-center items-center space-y-1 mt-4">
                        <Button
                            label="Import CSV"
                            iconLeft={<IconDocumentPlus size="lg" />}
                            onClick={handleUpload}
                            disabled={!file || fileTypeError}
                            color='primary'
                        />
                        <p className="text-gray-400 text-xs">OR</p>
                        <Button
                            label="Add Client Manually"
                            className="mt-6"
                            onClick={() => { setAddNewClientModal(true) }}
                            color="info"
                        />
                        <p className="text-gray-400 text-xs">OR</p>
                        <Button
                            label={"Continue without Importing"}
                            onClick={() => {
                                setLoadingExpertPortal(true);
                                window.location.assign(`${process.env.REACT_APP_EXPERT_PORTAL_URL}/dashboardsV2`);
                            }}
                            isLoading={loadingExpertPortal}
                        />
                    </div>
                    <Box sx={{ marginTop: 24 }}>
                        <div className="flex flex-row justify-start items-center space-x-2">
                            <LockOutlined fontSize="medium" />
                            <p className="text-xs">We use official, encrypted APIs to connect with QuickBooks—Paro never sees your login credentials.</p>
                        </div>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    )
}

export default Step1;